import React from "react";

const ROLE_OPTIONS = ["user", "recruiter", "admin", "manual"];

function ChipToggle({ value, list, onToggle, colorClasses }) {
  const active = list.includes(value);
  return (
    <button onClick={() => onToggle(value)} type="button"
      className={`text-xs px-2.5 py-1 rounded-full border ${active ? colorClasses : "border-white/10 text-slate-400"}`}>
      {value}
    </button>
  );
}

/** Shared dynamic-audience filter builder used by both Campaigns (ad-hoc segment)
 * and Lists (saved "dynamic" list = a reusable Segment) — one filter schema,
 * one UI, avoids duplicating this logic in two places. */
export default function SegmentFilterBuilder({ filter, setFilter, allTags, allCountries = [], allCities = [] }) {
  const toggleFrom = (key, value) => {
    const list = filter[key] || [];
    setFilter({ ...filter, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };

  return (
    <div data-testid="segment-filter-builder">
      <div className="flex items-center gap-4 mb-3">
        <label className="flex items-center gap-1.5 text-xs text-slate-300">
          <input type="radio" checked={filter.mode === "all"} onChange={() => setFilter({ ...filter, mode: "all" })} data-testid="segment-mode-all" /> All Subscribed Contacts
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-300">
          <input type="radio" checked={filter.mode === "filtered"} onChange={() => setFilter({ ...filter, mode: "filtered" })} data-testid="segment-mode-filtered" /> Filtered Segment
        </label>
      </div>
      {filter.mode === "filtered" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] text-slate-500 mb-1.5">Roles</p>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((r) => (
                <ChipToggle key={r} value={r} list={filter.roles || []} onToggle={(v) => toggleFrom("roles", v)} colorClasses="bg-indigo-500/20 border-indigo-400 text-indigo-300" />
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 mb-1.5">Tags</p>
            <div className="flex flex-wrap gap-2">
              {allTags.length === 0 && <span className="text-xs text-slate-500">No tags yet</span>}
              {allTags.map((tg) => (
                <ChipToggle key={tg} value={tg} list={filter.tags || []} onToggle={(v) => toggleFrom("tags", v)} colorClasses="bg-amber-500/20 border-amber-400 text-amber-300" />
              ))}
            </div>
          </div>
          {allCountries.length > 0 && (
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">Country</p>
              <div className="flex flex-wrap gap-2">
                {allCountries.map((c) => (
                  <ChipToggle key={c} value={c} list={filter.countries || []} onToggle={(v) => toggleFrom("countries", v)} colorClasses="bg-emerald-500/20 border-emerald-400 text-emerald-300" />
                ))}
              </div>
            </div>
          )}
          {allCities.length > 0 && (
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">City</p>
              <div className="flex flex-wrap gap-2">
                {allCities.map((c) => (
                  <ChipToggle key={c} value={c} list={filter.cities || []} onToggle={(v) => toggleFrom("cities", v)} colorClasses="bg-fuchsia-500/20 border-fuchsia-400 text-fuchsia-300" />
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[11px] text-slate-500 mb-1.5">Inactive for at least (days)</p>
            <input type="number" min="0" value={filter.inactive_days_gte || ""} onChange={(e) => setFilter({ ...filter, inactive_days_gte: e.target.value ? parseInt(e.target.value) : null })}
              data-testid="segment-inactive-days-input" placeholder="e.g. 30" className="w-32 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white" />
          </div>
        </div>
      )}
    </div>
  );
}
